/**
 * Client-safe helpers to walk a page-builder form `schema.root` tree.
 * Matches field collection in `handlePublicFormSubmit` (form_field + form_consent with names).
 */

export interface FormElementNode {
	children?: FormElementNode[];
	fieldType?: string;
	hidden?: boolean;
	label?: string;
	name?: string;
	options?: { label: string; value: string }[];
	presetValue?: string;
	required?: boolean;
	type: string;
	validation?: {
		max?: number;
		maxLength?: number;
		min?: number;
		pattern?: string;
	};
}

function walk(
	node: FormElementNode | undefined,
	visit: (n: FormElementNode) => void
) {
	if (!node || typeof node !== "object") {
		return;
	}
	visit(node);
	if (Array.isArray(node.children)) {
		for (const c of node.children) {
			walk(c, visit);
		}
	}
}

/** Collects all `form_field` and `form_consent` nodes that declare a `name`. */
export function collectNamedFormElements(root: unknown): FormElementNode[] {
	const out: FormElementNode[] = [];
	walk(root as FormElementNode | undefined, (node) => {
		if (
			(node.type === "form_field" || node.type === "form_consent") &&
			node.name
		) {
			out.push(node);
		}
	});
	return out;
}

/** True if the schema contains a `form_field` with the given `name`. */
export function formSchemaHasFormFieldNamed(
	root: unknown,
	name: string
): boolean {
	let found = false;
	walk(root as FormElementNode | undefined, (node) => {
		if (node.type === "form_field" && node.name === name) {
			found = true;
		}
	});
	return found;
}

/** `form_consent` element `name` values (for HubSpot consent UI dropdown). */
export function collectFormConsentFieldNames(root: unknown): string[] {
	const names: string[] = [];
	walk(root as FormElementNode | undefined, (node) => {
		if (node.type === "form_consent" && node.name) {
			names.push(node.name);
		}
	});
	return names;
}

/**
 * When HubSpot is enabled, the upsert can reach `email` if any of:
 * (1) a fieldMap value is `"email"`, (2) a fieldMap key is `email`, or
 * (3) a `form_field` in the schema is named `email`.
 */
export function isHubSpotEmailPathReachable(
	fieldMap: Record<string, string>,
	schemaRoot: unknown
): boolean {
	for (const v of Object.values(fieldMap)) {
		if (v === "email") {
			return true;
		}
	}
	if (Object.hasOwn(fieldMap, "email")) {
		return true;
	}
	return formSchemaHasFormFieldNamed(schemaRoot, "email");
}
